import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import {
  ArrowLeft, Store, UtensilsCrossed, Bell, AlertOctagon,
  Lightbulb, ChefHat, Package, Sparkles, BookOpen, Rocket, Mail, LogIn,
  Bike, WifiOff,
} from "lucide-react";

const IMG = (name) => `${process.env.REACT_APP_BACKEND_URL}/api/manual-img/${name}`;

// Reusable visual primitives ----------------------------------------------
function Section({ id, icon: Icon, kicker, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-16" data-testid={`manual-section-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-[#3e166c] text-white">
          <Icon className="h-5 w-5" />
        </span>
        <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae]">{kicker}</p>
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-black text-[#1a0f2e] mb-6 tracking-tight">{title}</h2>
      <div className="prose prose-purple max-w-none text-[#1a0f2e]">{children}</div>
    </section>
  );
}

function Shot({ src, alt, caption, mobile = false }) {
  return (
    <figure className="my-6">
      <div className={`mx-auto rounded-2xl overflow-hidden border border-[#b182ec]/40 shadow-sm bg-white ${mobile ? "max-w-xs" : "max-w-full"}`}>
        <img src={IMG(src)} alt={alt} className="w-full h-auto block" loading="lazy" />
      </div>
      {caption && <figcaption className="text-center text-xs text-[#7248ae] italic mt-2">{caption}</figcaption>}
    </figure>
  );
}

function Callout({ kind = "info", children }) {
  const map = {
    info:   { bg: "bg-[#f5edff]",  border: "border-[#7248ae]", bar: "before:bg-[#7248ae]" },
    ok:     { bg: "bg-emerald-50", border: "border-emerald-600", bar: "before:bg-emerald-600" },
    warn:   { bg: "bg-amber-50",   border: "border-amber-500",   bar: "before:bg-amber-500" },
    danger: { bg: "bg-rose-50",    border: "border-rose-600",    bar: "before:bg-rose-600" },
  }[kind];
  return (
    <div className={`relative pl-5 pr-4 py-3 my-4 rounded-r-xl ${map.bg} border ${map.border} before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${map.bar}`}>
      <p className="text-sm leading-relaxed text-[#1a0f2e] m-0">{children}</p>
    </div>
  );
}

function H3({ children }) {
  return <h3 className="font-display text-xl font-bold text-[#1a0f2e] mt-8 mb-2">{children}</h3>;
}

// Page --------------------------------------------------------------------
export default function ManualPage() {
  const TOC = [
    { id: "intro",    label: "Bienvenido",       icon: BookOpen },
    { id: "admin",    label: "Admin",            icon: Store },
    { id: "menu",     label: "Menú",             icon: UtensilsCrossed },
    { id: "inv",      label: "Inventario",       icon: Package },
    { id: "recipes",  label: "Recetas",          icon: ChefHat },
    { id: "predict",  label: "Predicción IA",    icon: Sparkles },
    { id: "delivery", label: "Delivery",         icon: Bike },
    { id: "waiter",   label: "Mesero",           icon: UtensilsCrossed },
    { id: "push",     label: "Push (PWA)",       icon: Bell },
    { id: "offline",  label: "Sin conexión",     icon: WifiOff },
    { id: "expired",  label: "Suscripción",      icon: AlertOctagon },
    { id: "tips",     label: "Tips de oro",      icon: Lightbulb },
    { id: "cta",      label: "Empezar",          icon: Rocket },
  ];

  return (
    <div className="min-h-screen bg-[#fcfbfd]" data-testid="manual-page">
      {/* Hero / Cover */}
      <header className="bg-[#1a0f2e] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="manual-back"
            className="inline-flex items-center gap-2 text-sm text-[#d8baff] hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />Volver
          </Link>
          <div className="flex items-center gap-2">
            <Logo size={28} variant="plain" />
            <span className="font-display font-bold tracking-tight">oRdeN</span>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 sm:px-10 pb-20 pt-10">
          <p className="text-xs uppercase tracking-[0.4em] text-[#b182ec] mb-4">Manual de usuario ilustrado</p>
          <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tight leading-[0.95]">
            oRdeN<span className="text-[#b182ec]">.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/80">
            PWA SaaS para tomar pedidos en restaurantes. Una app, dos roles, sin fricción.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Admin", "Mesero", "Delivery"].map(r => (
              <span key={r} className="px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-widest text-[#d8baff]">{r}</span>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 -mt-10 mb-10">
        <nav className="rounded-2xl bg-white border border-[#b182ec]/40 shadow-lg p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="manual-toc">
          {TOC.map(({ id, label, icon: Icon }) => (
            <a key={id} href={`#${id}`}
               className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#3e166c] hover:bg-[#3e166c]/10 transition-colors">
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{label}</span>
            </a>
          ))}
        </nav>
      </div>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 pb-24">
        {/* INTRO */}
        <Section id="intro" icon={BookOpen} kicker="Comienza aquí" title="Bienvenido a oRdeN">
          <p>
            <strong>oRdeN</strong> es una PWA SaaS que se instala como app nativa en cualquier celular, tablet o computadora.
            Como administrador del restaurante, tienes todo lo necesario para operar: menú, mesas, inventario, recetas con costos automáticos,
            predicción de compras con IA, pedidos a domicilio y reportes.
            La suscripción dura <strong>30 días</strong> y se renueva con un clic desde la pantalla de bloqueo.
          </p>
          <Callout kind="info">
            <strong>PWA-first.</strong> Después del primer login, instala la app desde el navegador:{" "}
            <strong>Compartir → Agregar a pantalla de inicio</strong> (iOS) o el ícono ⊕ de la barra (Android/Chrome).
          </Callout>
          <H3>Pantalla de inicio</H3>
          <Shot src="01_login.jpeg" alt="Pantalla de login" caption="Login con identidad visual oRdeN y cuentas demo visibles para probar cada rol." />
        </Section>

        {/* ADMIN */}
        <Section id="admin" icon={Store} kicker="Rol" title="Admin — Dueño del restaurante">
          <p>
            El admin configura todo lo necesario para operar y revisa dashboards, predicciones y reportes. Al entrar verá su{" "}
            <strong>Dashboard</strong> con KPIs en tiempo real.
          </p>
          <Shot src="05_admin_dashboard.jpeg" alt="Admin Dashboard" caption="Dashboard · ventas hoy, semana, mesas ocupadas y pedidos pagados." />

          <H3>Mesas</H3>
          <p>
            Crea las mesas físicas con número y capacidad. Cada mesa muestra su estado en tiempo real (libre, ocupada) y el total cobrado a cuenta abierta.
          </p>
          <Shot src="06_admin_tables.jpeg" alt="Admin Mesas" caption="Mesas · vista del salón con totales en tiempo real." />
        </Section>

        {/* MENU */}
        <Section id="menu" icon={UtensilsCrossed} kicker="Catálogo" title="Menú — 3 formas de cargarlo">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Manual</strong>: «Nuevo platillo» → nombre, precio, categoría, foto, descripción.</li>
            <li><strong>Carga masiva por Excel</strong>: descarga la plantilla → llena Nombre, Precio, Categoría, Descripción, URL Foto, e incluso las recetas (Insumos · Consumibles · Consumibles Delivery) → sube el archivo.</li>
            <li><strong>Modificadores</strong> por platillo: grupos <em>Radio</em> (selección única, ej. Salsa: Roja / Verde / Sin) o <em>Checkbox</em> (múltiple, ej. Extras: Queso · Aguacate · Tocino).</li>
          </ul>
          <Callout kind="ok">
            <strong>Excel inteligente.</strong> Si en la columna de Insumos mencionas un ingrediente que aún no existe en tu inventario, oRdeN lo crea automáticamente con costo $0. Después solo entras a Inventario una vez y completas los precios.
          </Callout>
          <Shot src="07_admin_menu.jpeg" alt="Admin Menú" caption="Menú · platillos organizados por categoría con precio visible." />
        </Section>

        {/* INVENTARIO */}
        <Section id="inv" icon={Package} kicker="Operación" title="Inventario">
          <p>
            Materias primas con stock, mínimo, costo unitario y proveedor. El valor total del inventario se calcula en vivo.
            Botones contextuales: <strong>Reabastecer</strong> (entrada de mercancía) y <strong>Merma</strong> (desperdicio / ajuste manual).
            Filtros por estado y búsqueda por nombre o proveedor.
          </p>
          <Callout kind="info">
            <strong>Carga masiva por Excel.</strong> Botones <strong>Plantilla</strong> e <strong>Importar Excel</strong> arriba a la derecha.
            Si el nombre del ingrediente ya existe se <strong>actualiza</strong>; si no, se <strong>crea</strong>. Ideal para cargar 50+ ingredientes en 2 minutos.
          </Callout>
          <Shot src="08_admin_inventory.jpeg" alt="Admin Inventario" caption="Inventario · ingredientes con valor total y filtros activos." />
        </Section>

        {/* RECETAS */}
        <Section id="recipes" icon={ChefHat} kicker="Vínculo menú ↔ inventario" title="Recetas">
          <p>
            Por cada platillo, define los ingredientes y cantidades. <strong>Conversión automática</strong>: si el ingrediente está en kg y la receta usa gramos,
            el sistema convierte (ej. Carne $150/kg, receta 30 gr → costo $4.50). <strong>Validaciones inteligentes</strong> avisan unidades incompatibles o costos sospechosamente altos.{" "}
            <strong>Sugerencia de precio</strong> aparece cuando el margen es bajo y permite aplicarlo con un clic.
          </p>
          <H3>3 listas por platillo</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Insumos</strong>: ingredientes principales (café, leche, jamón). Se descuentan en TODOS los pedidos —mesa o delivery.</li>
            <li><strong>Consumibles</strong>: vasos, tapas, servilletas. Se descuentan SOLO al cobrar una mesa.</li>
            <li><strong>Consumibles Delivery</strong>: empaque para llevar (vaso, tapa, bolsa). REEMPLAZA a «Consumibles» cuando el pedido es delivery.</li>
          </ul>
          <Shot src="09_admin_recipes.jpeg" alt="Admin Recetas" caption="Recetas · platillos con margen calculado y otros sin receta configurada." />
          <Callout kind="ok">
            <strong>Tip de oro.</strong> Configura siempre las recetas. Sin ellas no hay descuento automático de stock, ni predicciones, ni sugerencias de precio.
            Con ellas, oRdeN se vuelve un copiloto financiero.
          </Callout>
        </Section>

        {/* PREDICCION */}
        <Section id="predict" icon={Sparkles} kicker="Machine Learning" title="Predicción de Compras (Inventario IA)">
          <p>
            El motor de IA analiza los últimos 30 días de consumo y genera predicciones por ingrediente: días hasta agotarse, cuánto comprar, tendencia semanal.
            Horizonte ajustable a 7 / 14 / 30 días.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>KPIs en cabecera</strong>: ingredientes, alertas activas, valor de inventario, compra sugerida, merma 30d.</li>
            <li><strong>Predicción por ingrediente</strong>: tabla con stock, consumo/día, tendencia (% crecimiento), días restantes, cantidad a comprar y costo.</li>
            <li><strong>Top consumidos</strong>: ranking de uso de la última semana.</li>
            <li><strong>Márgenes por platillo</strong>: cuánto cuesta producir cada platillo y qué % de margen genera.</li>
            <li><strong>Informe semanal de compras</strong>: top-3 compras urgentes + costo total. Se envía automático cada lunes 14:00 UTC y manualmente con el botón <strong>Informe semanal</strong>.</li>
            <li><strong>Export CONTPAQi</strong>: descarga CSV con movimientos en formato contable.</li>
          </ul>
          <Shot src="10_admin_predict.jpeg" alt="Predicción IA" caption="Predicción · KPIs + tabla por ingrediente + márgenes por platillo." />
        </Section>

        {/* DELIVERY */}
        <Section id="delivery" icon={Bike} kicker="Pedidos a domicilio" title="Delivery">
          <p>
            Acepta pedidos a domicilio desde tu propio storefront. Tus clientes piden, tú cobras por email o WhatsApp con tu link de pago favorito (Stripe, Mercado Pago, transferencia).
          </p>
          <H3>Cómo activarlo (1 vez)</H3>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Ve a <strong>Ajustes → Cobro & Delivery</strong>.</li>
            <li>Activa el toggle <strong>«Aceptar pedidos por delivery»</strong> → oRdeN genera un link público único:{" "}
              <code className="font-mono text-[#3e166c] text-xs">or-den.com/r/tu-restaurante</code>.</li>
            <li>Configura tu <strong>link de cobro</strong> (URL de Stripe, Mercado Pago, etc.) e <strong>instrucciones</strong> opcionales para el cliente.</li>
            <li>Copia el link público y compártelo en WhatsApp, redes sociales o redes del local.</li>
          </ol>

          <H3>Flujo del pedido</H3>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Cliente abre tu link</strong>, ve tu menú categorizado, agrega platillos al carrito, se registra y envía pedido con dirección + referencias.</li>
            <li>Tú recibes el pedido en <strong>Admin → Delivery</strong> con notificación push + sonido instantáneo.</li>
            <li>Editas <strong>costo de envío</strong> y <strong>descuento</strong> si aplica (modal «Envío/Descuento» en cada pedido).</li>
            <li>Clic en <strong>«Cobrar»</strong> → se envía email con desglose + link de pago, y se abre <strong>WhatsApp</strong> con el mensaje pre-armado al cliente.</li>
            <li>Avanzas estados: <em>Pagado → Preparando → En camino → Entregado</em>. Cada cambio se registra con timestamp.</li>
            <li>Al marcar <strong>«Pagado»</strong>, el inventario se descuenta automáticamente con <em>Insumos + Consumibles Delivery</em>.</li>
          </ol>
          <Callout kind="info">
            <strong>WhatsApp inteligente.</strong> El mensaje incluye saludo personalizado, desglose ítem por ítem, subtotal/envío/descuento/total con negritas, tu link de pago y tus instrucciones. Auto-prefija lada México (+52).
          </Callout>
          <Callout kind="warn">
            <strong>Los meseros NO ven Delivery.</strong> Solo el admin gestiona los pedidos a domicilio para evitar conflictos con el servicio de mesa.
          </Callout>
        </Section>

        {/* WAITER */}
        <Section id="waiter" icon={UtensilsCrossed} kicker="Rol" title="Mesero">
          <p>
            Interfaz simplificada en formato móvil, solo con lo que el mesero necesita en el piso: mesas, pedidos e historial. Sin distracciones.
          </p>

          <H3>El flujo completo en 10 segundos</H3>
          <p className="text-sm text-[#7248ae]">
            Mesas → abrir mesa → agregar platillo → confirmar → cobrar → split bill. Loop continuo.
          </p>
          <figure className="my-6 flex justify-center">
            <div className="relative inline-block rounded-[2rem] overflow-hidden border-[6px] border-[#1a0f2e] shadow-[0_20px_60px_-10px_rgba(62,22,108,0.4)] bg-[#1a0f2e]">
              <img
                src={IMG("waiter_demo.gif")}
                alt="Demo animada del flujo del mesero"
                className="block w-[260px] sm:w-[300px] h-auto"
                loading="lazy"
                data-testid="manual-waiter-demo-gif"
              />
              <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-4 rounded-b-xl bg-[#1a0f2e]" />
            </div>
          </figure>

          <div className="grid sm:grid-cols-2 gap-6 my-6">
            <Shot src="11_waiter_tables.jpeg" alt="Mesero Mesas" caption="Mesas · vista del salón en móvil con totales en tiempo real." mobile />
            <Shot src="12_waiter_order.jpeg"  alt="Mesero Pedido" caption="Pedido abierto · agregar ítems, ajustar cantidad, confirmar e imprimir." mobile />
          </div>

          <H3>Tomar un pedido</H3>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Mesas</strong> → seleccionar una mesa libre u ocupada.</li>
            <li><strong>Agregar platillos</strong>: tocar categoría → tocar platillo. Si tiene modificadores, elegir y «Agregar».</li>
            <li>Ajustar cantidad con + / – y agregar nota opcional por ítem («sin cebolla», «término medio»).</li>
            <li><strong>Confirmar pedido</strong> → se imprime la comanda y se envía a cocina.</li>
            <li>Agregar más ítems durante el servicio si el cliente pide algo extra.</li>
          </ol>

          <H3>Cerrar la cuenta</H3>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>«Cobrar»</strong> en la mesa.</li>
            <li>Elegir método de pago: Efectivo · Tarjeta · Transferencia.</li>
            <li><strong>Split bill</strong> — <em>Por persona</em>: divide en partes iguales. <em>Por items</em>: arrastra cada ítem al ticket de quien lo consume.</li>
            <li><strong>Imprimir ticket</strong>: selector de formato (Térmica 80mm / 58mm / Hoja A4). El navegador abre el diálogo nativo donde eliges la impresora.</li>
            <li>Al cobrar, el inventario se descuenta automáticamente según las recetas.</li>
          </ol>
        </Section>

        {/* PUSH */}
        <Section id="push" icon={Bell} kicker="Tiempo real" title="Notificaciones Push (PWA)">
          <p>
            Solo para Admin. Tras el primer login aparece un banner pidiendo permiso. Al aceptar,
            recibes notificaciones en el dispositivo aún con la app cerrada.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Pedidos delivery nuevos</strong> al instante (con sonido en la pestaña abierta).</li>
            <li><strong>Alertas de stock crítico</strong> con deep-link al ingrediente exacto.</li>
            <li><strong>Avisos del sistema</strong> (cobros, errores de impresión).</li>
          </ul>
        </Section>

        {/* OFFLINE */}
        <Section id="offline" icon={WifiOff} kicker="Modo sin conexión" title="Cuando se cae el WiFi">
          <p>
            oRdeN sigue funcionando aunque pierdas internet temporalmente.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>App abre offline</strong>: el último estado se carga desde cache (menú, mesas, inventario, pedidos abiertos).</li>
            <li><strong>Banner amarillo</strong> arriba avisa «Sin conexión — modo solo lectura».</li>
            <li><strong>Lecturas funcionan</strong>: puedes navegar, consultar y mostrar datos.</li>
            <li><strong>Escrituras se bloquean</strong> con un mensaje claro hasta que vuelva el WiFi (no se pierden datos por intentar guardar offline).</li>
            <li><strong>Indicador «Sincronizado hace X»</strong> arriba al centro muestra cuándo fue la última sincronización exitosa.</li>
            <li><strong>Botón «Forzar sincronización»</strong> en el panel de sync por si sientes que algo no se actualizó.</li>
          </ul>
        </Section>

        {/* EXPIRED */}
        <Section id="expired" icon={AlertOctagon} kicker="Suscripción" title="Cuando vence tu suscripción">
          <p>La suscripción dura 30 días. Cuando vence:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pierdes acceso a TODO (menú, mesas, inventario, reportes, delivery).</li>
            <li>Solo verás la pantalla <strong>«Tu suscripción ha vencido»</strong> con la fecha de tu última activación.</li>
            <li>Botón único <strong>«Solicitar activación»</strong> → envía email automático al equipo de soporte de oRdeN.</li>
            <li>El equipo extiende tu suscripción +30 días con un clic y vuelves a operar al instante.</li>
          </ul>
          <Shot src="13_expired.jpeg" alt="Suscripción vencida" caption="Pantalla de bloqueo · única opción: solicitar reactivación." />
        </Section>

        {/* TIPS */}
        <Section id="tips" icon={Lightbulb} kicker="Recomendaciones" title="Tips de oro">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>PWA-first</strong>: instala la app y úsala fullscreen — los meseros operan mejor incluso con conexión intermitente.</li>
            <li><strong>Excel de menú</strong>: la forma más rápida de cargar 50+ platillos en 2 minutos —incluye recetas en la misma plantilla.</li>
            <li><strong>Recetas = magia</strong>: configúralas siempre con las 3 listas (Insumos · Consumibles · Delivery). Te dan inventario automático + predicciones + sugerencias de precio.</li>
            <li><strong>Informe semanal</strong>: léelo cada lunes. Te dice exactamente cuánto comprar para no quedarte sin stock.</li>
            <li><strong>Delivery por WhatsApp</strong>: comparte tu link público con un mensaje tipo «🛵 Pide a domicilio aquí 👉 or-den.com/r/tu-restaurante» y verás cómo entran pedidos.</li>
            <li><strong>Backups</strong>: el export CSV CONTPAQi es ideal para tu contador.</li>
          </ul>
        </Section>

        {/* CTA — Listo para empezar */}
        <section id="cta" data-testid="manual-cta" className="my-16 scroll-mt-24">
          <div className="relative overflow-hidden rounded-3xl bg-[#1a0f2e] text-white px-8 py-12 sm:px-14 sm:py-16">
            <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#7248ae]/30 blur-3xl" />
            <div aria-hidden className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-[#b182ec]/20 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-[0.3em] text-[#d8baff]">
                <Rocket className="h-3.5 w-3.5" />Empieza hoy
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-5 leading-[1.05]">
                ¿Listo para empezar?
              </h2>
              <p className="mt-4 text-lg text-white/80 max-w-xl">
                Crea tu restaurante en <strong className="text-white">2 minutos</strong>. Sin tarjeta de crédito, sin instalaciones —
                solo abre la app desde el navegador y empieza a tomar pedidos.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/login"
                  data-testid="cta-login"
                  className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-[#1a0f2e] font-semibold hover:bg-[#d8baff] transition-colors"
                >
                  <LogIn className="h-4 w-4" />Iniciar sesión
                </Link>
                <a
                  href="mailto:hola@or-den.com?subject=Quiero%20probar%20oRdeN"
                  data-testid="cta-contact"
                  className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                >
                  <Mail className="h-4 w-4" />Contactar al equipo
                </a>
              </div>
              <p className="mt-6 text-xs text-white/50 tracking-wider">
                hola@or-den.com · respondemos en menos de 24 hrs
              </p>
            </div>
          </div>
        </section>

        <footer className="border-t border-[#b182ec]/30 pt-8 text-center text-sm text-[#7248ae]">
          ¿Dudas o sugerencias? Escríbenos a <a href="mailto:hola@or-den.com" className="underline">hola@or-den.com</a>.
          <p className="mt-2 text-xs">© 2026 oRdeN — PWA SaaS para restaurantes</p>
        </footer>
      </main>
    </div>
  );
}
